import { api } from './client';

/**
 * Endpoints implemented by auth-service (/auth/*).
 * See services/auth-service/internal/handler.
 */

export type Role = 'admin' | 'manager' | 'curator' | 'student';

export type AuthUser = {
  id: string;
  email: string;
  full_name: string;
  role: Role;
  avatar_url?: string;
  /** Interface language preference persisted server-side (TZ §5.12). */
  lang?: string;
  /**
   * Effective enabled feature keys for the current user's tariff.
   * Keys: lessons, live, attendance, speaking_peers, speaking_ai, chat,
   * vocab, cefr. For admin/curator/manager this may be absent or contain a
   * `*` sentinel meaning all-access. When undefined (not yet loaded / backend
   * didn't send), treat as ALL-access — never hide features due to missing data.
   */
  features?: string[];
  /**
   * Новое имя `features` в ответе ученика (договор student-api.md §4). Пока
   * служба отдаёт одно из двух — читать только через `userSections`
   * (`lib/studentFields.ts`), не напрямую.
   */
  sections?: string[];
  /**
   * Тариф ученику ещё НЕ назначен — он ждёт начала обучения.
   *
   * ⚠️ По этому признаку интерфейс решает, что делать с закрытым разделом:
   * ждёт начала → показать замок «Обучение ещё не началось»;
   * тариф есть, но узкий → просто СПРЯТАТЬ, без замка (решение заказчика
   * 04.09.2026: «то, что недоступно в тарифах, просто прячем»).
   */
  awaiting_start?: boolean;
  /**
   * Момент подтверждения почты; null/undefined — адрес не подтверждён.
   * Одно и то же поле приходит и в ответе входа, и в /auth/me.
   */
  email_verified_at?: string | null;
  /**
   * Знает ли человек свой пароль. У зарегистрировавшихся по коду из письма
   * пароля нет — им предлагается ЗАДАТЬ его, а не сменить.
   * Отсутствие поля (старый сервер) считаем «пароль есть»: это прежнее
   * поведение, и оно ничего не ломает.
   */
  password_set?: boolean;
};

/** Tariff-gated feature keys understood by the frontend. */
export type FeatureKey =
  | 'lessons'
  | 'live'
  | 'attendance'
  | 'speaking_peers'
  | 'speaking_ai'
  | 'chat'
  | 'vocab'
  | 'cefr'
  // Отдельные тренажёры (01.09.2026): каждый закрывается своим тумблером.
  // Общий 'ielts' остаётся ради тарифов, заведённых раньше: на сервере он
  // означает «все тренажёры» — иначе платящие потеряли бы их разом.
  | 'ielts_reading'
  | 'ielts_listening'
  | 'ielts_writing'
  | 'ielts_speaking'
  | 'mock'
  // sprint — короткая тренировка сразу по нескольким навыкам (04.09.2026).
  // До этого дня спринт не был закрыт ничем: ни тумблера, ни проверки.
  | 'sprint'
  | 'ielts'
  // Разбор письменных работ ИИ. Отделён от `ielts` 24.08.2026: чтение и
  // слушание почти бесплатны, а каждое эссе читает модель.
  | 'writing_ai';

export type AuthTokens = {
  access_token: string;
  refresh_token: string;
};

export type LoginResponse = AuthTokens & { user: AuthUser };

export const authApi = {
  async login(email: string, password: string): Promise<LoginResponse> {
    const { data } = await api.post('/auth/login', { email, password });
    return data;
  },

  // Backend POST /auth/register returns ONLY the created user (no tokens).
  // See services/auth-service/internal/handler/auth_handler.go Register().
  async register(payload: {
    full_name: string;
    email: string;
    /** Пароля может не быть: тогда человек входит кодом из письма. */
    password?: string;
    /** На сайте телефон обязателен, в приложении — нет (правило Apple 5.1.1). */
    phone?: string;
  }): Promise<AuthUser> {
    const { data } = await api.post('/auth/register', payload);
    return data;
  },

  /**
   * Просит выслать код для входа. Ответ ОДИНАКОВ и для существующего адреса, и
   * для несуществующего — по нему нельзя узнать, учится ли человек в школе.
   * Поэтому «письмо отправлено» показываем всегда, даже когда письма не было.
   */
  async requestLoginCode(email: string): Promise<{ retry_after_seconds: number; expires_in_seconds: number }> {
    const { data } = await api.post('/auth/login/code/request', { email });
    return data;
  },

  /** Вход кодом из письма: возвращает такую же сессию, как вход паролем. */
  async loginByCode(email: string, code: string): Promise<LoginResponse> {
    const { data } = await api.post('/auth/login/code', { email, code });
    return data;
  },

  async logout(refresh_token: string) {
    await api.post('/auth/logout', { refresh_token });
  },

  async forgotPassword(email: string) {
    await api.post('/auth/password/forgot', { email });
  },

  async resetPassword(token: string, new_password: string) {
    await api.post('/auth/password/reset', { token, new_password });
  },

  async me(): Promise<AuthUser> {
    const { data } = await api.get('/auth/me');
    return data;
  },

  async updateMe(payload: { full_name?: string; lang?: string }): Promise<AuthUser> {
    const { data } = await api.put('/auth/me', payload);
    return data;
  },

  async updateAvatar(avatar_url: string): Promise<AuthUser> {
    const { data } = await api.put('/auth/me/avatar', { avatar_url });
    return data;
  },

  // PUT /auth/password/update — change own password while authenticated.
  // Backend (auth_handler.go UpdatePassword) verifies the old password,
  // enforces complexity + no-reuse-of-last-3, and returns no payload.
  /**
   * Задать пароль впервые — для тех, кто регистрировался по коду из письма и
   * старого пароля не знает. Работает один раз: как только пароль есть, дальше
   * только смена со старым.
   */
  async setPassword(new_password: string): Promise<void> {
    await api.post('/auth/password/set', { new_password });
  },

  async updatePassword(payload: { old_password: string; new_password: string }): Promise<void> {
    await api.put('/auth/password/update', payload);
  },

  // --- Подтверждение почты кодом ---
  // Ссылку в письме приложение перехватить не умеет (universal links не
  // настроены), поэтому подтверждаем шестизначным кодом: человек переписывает
  // его в открытое приложение.

  /** Просит новый код. Отвечает, через сколько секунд можно просить следующий. */
  async requestEmailCode(): Promise<{ retry_after_seconds: number; expires_in_seconds: number }> {
    const { data } = await api.post('/auth/email/verify/request');
    return data;
  },

  async verifyEmail(code: string): Promise<void> {
    await api.post('/auth/email/verify', { code });
  },

  /** Правит опечатку в адресе, пока он не подтверждён, и высылает новый код. */
  async changeUnverifiedEmail(email: string): Promise<void> {
    await api.post('/auth/email/change', { email });
  },
};
